// TOEFL iBT Mock 5 — Listening (2026 format, multistage adaptive, advanced)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Like Mocks 6 and 7, this set leans on indirect replies, implied meaning,
// speaker attitude and "why does the speaker mention…" questions, and wrong
// options often reuse words from the audio to punish word-matching.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", W("I don't suppose you've seen my calculator anywhere?"), [
      "I suppose it calculates quickly.",
      "I haven't seen you anywhere today.",
      "Did you check under the papers on the lab bench?",
      "Yes, I suppose I could.",
    ], 2),
    response("m1-r2", M("The bookstore's charging forty dollars for a used copy of the textbook."), [
      "You might find it cheaper online.",
      "I used the textbook last year.",
      "Forty students are in the class.",
      "The bookstore is charging my phone.",
    ], 0),
    response("m1-r3", W("I can't believe I left my presentation on the bus."), [
      "The bus leaves every twenty minutes.",
      "I believe the presentation is today.",
      "I left mine at home too.",
      "Didn't you email a copy to yourself?",
    ], 3),
    response("m1-r4", M("Wasn't the group meeting supposed to start at three?"), [
      "Yes, I'm supposed to be in a group.",
      "Jun texted that he's running a bit late.",
      "It's a three-person group.",
      "The meeting room has three windows.",
    ], 1),
    response("m1-r5", W("You've been working on that essay all day. Why not take a break?"), [
      "I worked at the café last summer.",
      "My essay has a break in the middle.",
      "I'd love to, but it's due at midnight.",
      "All day? It's only noon.",
    ], 2),
    response("m1-r6", M("I'd have joined the hiking club if I'd known about it sooner."), [
      "I've known about it for a while.",
      "It's not too late. They're still accepting members.",
      "The hiking trail is sooner than you think.",
      "Yes, the club joined last year.",
    ], 1),
    response("m1-r7", W("There's no way I'm finishing this lab report by Friday."), [
      "Why don't you ask Dr. Hale for a short extension?",
      "There's a way to the lab through the library.",
      "I finished my report last Friday.",
      "The lab is closed on weekends.",
    ], 0),
    response("m1-r8", M("Funny how the professor never mentioned the reading would be on the exam."), [
      "It was a very funny reading.",
      "She mentioned it to the professor.",
      "The exam is in the main hall.",
      "Actually, it was in the syllabus all along.",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Professor's Office: A Grade Question",
      lines: [
        M("Professor Alvarez, do you have a moment? I wanted to ask about my midterm grade. I lost fifteen points on the essay question, and I'm not sure why."),
        W("Let's have a look. Ah, yes. Your essay describes the causes of the trade agreement very clearly, but the question asked you to evaluate them."),
        M("Isn't that basically the same thing?"),
        W("Not quite. Describing tells me what happened. Evaluating means judging which causes mattered most and giving reasons. You listed four causes but treated them as equally important."),
        M("I see. So I should have argued that one cause was more important than the others."),
        W("Exactly, and supported that with evidence from the readings. I can't change the grade, but the final exam has a similar question. If you'd like, send me a practice paragraph next week, and I'll tell you whether it's evaluating rather than describing."),
        M("That would really help. Thank you."),
      ],
      questions: [
        {
          question: "Why did the student go to see the professor?",
          options: [
            "To ask for an extension on an essay",
            "To find out why he lost points on a test",
            "To request a change to his final exam date",
            "To discuss a trade agreement for a project",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, what was the main weakness of the student's essay?",
          options: [
            "It did not mention enough causes.",
            "It contained factual errors.",
            "It did not judge the relative importance of the causes.",
            "It was much shorter than required.",
          ],
          correct: 2,
        },
        {
          question: "What does the professor offer to do?",
          options: [
            "Review a short piece of practice writing",
            "Raise the student's midterm grade",
            "Let the student retake the midterm",
            "Give the student the final exam question early",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Emergency Alert System Test",
      lines: [
        W("Good morning. On Thursday at eleven a.m., the university will conduct its annual test of the campus emergency alert system. During the test, you'll receive a text message and an email, and outdoor sirens will sound for approximately ninety seconds. You do not need to take any action, and classes will continue as normal. However, if you do not receive the text message, it means your phone number is missing or out of date in the student portal. Please update it by the end of the week, because during a real emergency, text messages are the fastest way we can reach you. Faculty are asked to remind students in their eleven o'clock classes that the sirens are part of a test."),
      ],
      questions: [
        {
          question: "What should students do if they do not receive the text message?",
          options: [
            "Leave the building immediately",
            "Call the campus emergency office",
            "Ignore it because it is only a test",
            "Update their contact details in the student portal",
          ],
          correct: 3,
        },
        {
          question: "Why are faculty asked to speak to students in eleven o'clock classes?",
          options: [
            "To prevent students from thinking a real emergency is happening",
            "To collect students' phone numbers",
            "To cancel classes during the test",
            "To explain how to evacuate the building",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Materials Science: Why Roman Concrete Lasts",
      lines: [
        M("Many Roman harbors and buildings made of concrete are still standing after two thousand years, while modern concrete structures often begin to crack within decades. For a long time, the durability of Roman concrete was something of a mystery. One ingredient clearly mattered: volcanic ash, which the Romans mixed with lime. But recent research has pointed to something that earlier scientists dismissed as a flaw. Roman concrete contains small white lumps of lime, and these were long assumed to be evidence of careless mixing. It now appears they were the result of a deliberate technique called hot mixing, in which lime was added in a highly reactive form that produced intense heat. Here's why that matters. When a crack forms and water seeps in, the water reaches these lime lumps, dissolves some of the calcium, and the calcium then recrystallizes inside the crack, sealing it. In other words, the material can partially repair itself. In marine structures, seawater actually triggers the growth of new minerals that make the concrete stronger over time, the opposite of what happens to modern concrete. Engineers are now testing whether similar self-healing properties could be built into modern mixes, which could greatly reduce the environmental cost of constantly replacing concrete."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "Why Roman builders preferred harbors to other structures",
            "The reasons Roman concrete has remained durable",
            "How volcanic eruptions damaged Roman buildings",
            "The history of modern concrete production",
          ],
          correct: 1,
        },
        {
          question: "How did earlier scientists interpret the white lumps in Roman concrete?",
          options: [
            "As a sign that the concrete had been badly mixed",
            "As evidence of volcanic ash",
            "As a later addition by modern engineers",
            "As a decoration added by Roman builders",
          ],
          correct: 0,
        },
        {
          question: "According to the professor, what happens when water enters a crack in Roman concrete?",
          options: [
            "The concrete expands and the crack becomes wider.",
            "The volcanic ash dissolves completely.",
            "Calcium from the lime fills the crack as it recrystallizes.",
            "The lime absorbs the water and loses its strength.",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor mention the environmental cost of replacing concrete?",
          options: [
            "To argue that Roman builders were concerned about pollution",
            "To criticize the use of volcanic ash",
            "To explain why hot mixing was abandoned",
            "To suggest why self-healing concrete could be valuable today",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", M("I'd hardly call three pages of notes 'barely studying.'"), [
      "I'd call you later about the notes.",
      "Three pages is the limit for the essay.",
      "Well, compared with Lena's twenty, it feels like nothing.",
      "I hardly studied for the test either.",
    ], 2),
    response("up-r2", W("If anyone can get that grant, it's you."), [
      "Thanks, but the competition is pretty tough this year.",
      "Anyone can apply for it online.",
      "I can get you a copy if you want.",
      "It's for the science department.",
    ], 0),
    response("up-r3", M("Not to be difficult, but didn't we agree to split the work evenly?"), [
      "It wasn't difficult work.",
      "We split the bill evenly last time.",
      "Yes, we agreed it was even.",
      "You're right. I'll take over the data section.",
    ], 3),
    response("up-r4", W("I'm beginning to wonder whether this experiment was worth all the effort."), [
      "It was a wonderful experiment.",
      "Give it time. Results often look messy at first.",
      "I'm beginning the experiment tomorrow.",
      "It was worth about fifty dollars.",
    ], 1),
    response("up-r5", M("So the field trip is off, then?"), [
      "Only postponed, until the roads are cleared.",
      "I turned off the lights after the trip.",
      "The field is just off the main road.",
      "Yes, I tripped on the way there.",
    ], 0),
    {
      id: "up-conv",
      type: "conversation",
      title: "Teaching Assistant: A Group Project Problem",
      lines: [
        W("Hi, Tom. I'm a bit uncomfortable bringing this up, but one member of my project group hasn't contributed anything in three weeks."),
        M("Thanks for telling me. Have you talked to him directly?"),
        W("We sent messages, but he only replied once, saying he'd been busy. Now the rest of us are doing his section so we don't miss the deadline."),
        M("I understand why you'd do that, but I'd actually encourage you not to. If you complete his part, there's no record that anything went wrong, and everyone gets the same grade."),
        W("So what should we do?"),
        M("Keep doing your own sections, and keep a simple log of who did what. At the end, every student submits a peer evaluation, and the professor takes those seriously, especially when they're backed up by evidence. I'll also send him a message reminding him of his responsibilities. Sometimes hearing it from a TA makes a difference."),
        W("That's a relief. I didn't want to get anyone in trouble, but it didn't seem fair."),
      ],
      questions: [
        {
          question: "What is the woman's problem?",
          options: [
            "Her group cannot agree on a topic.",
            "One group member is not doing his share of the work.",
            "The project deadline has been moved earlier.",
            "She has been too busy to finish her section.",
          ],
          correct: 1,
        },
        {
          question: "Why does the TA advise the group not to complete the missing section?",
          options: [
            "Because the professor will deduct points for it",
            "Because the section is not required for the project",
            "Because it would hide the problem from the professor",
            "Because the student promised to finish it later",
          ],
          correct: 2,
        },
        {
          question: "What does the TA imply about the peer evaluations?",
          options: [
            "They are more convincing when supported by records.",
            "They are rarely read by the professor.",
            "They must be submitted before the project.",
            "They can only be written by the TA.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Economics: Managing Shared Resources",
      lines: [
        W("In nineteen sixty-eight, the ecologist Garrett Hardin published an influential essay called The Tragedy of the Commons. Imagine a pasture open to all herders. Each herder gains the full benefit of adding one more animal, but the cost of overgrazing is shared by everyone. So each person, acting rationally, keeps adding animals, until the pasture is destroyed. Hardin concluded that shared resources must either be divided into private property or controlled by the government. For decades, this was treated almost as a law of nature. The political economist Elinor Ostrom challenged it, not with a theory, but with fieldwork. She studied fishing grounds, forests, and irrigation systems around the world that communities had managed successfully for centuries without privatization or outside control. Swiss villages, for instance, had shared high mountain pastures since the Middle Ages without destroying them. Ostrom identified common features of these systems: clear boundaries defining who may use the resource, rules adapted to local conditions, monitoring by the users themselves, and gradually increasing penalties for those who break the rules. Her work showed that the tragedy Hardin described is a real risk, but not an inevitable outcome. In two thousand nine, she became the first woman to receive the Nobel Prize in economics."),
      ],
      questions: [
        {
          question: "According to Hardin, why do individuals overuse a shared resource?",
          options: [
            "They do not understand how the resource works.",
            "Governments encourage them to use more.",
            "They are forced to compete with outsiders.",
            "Each gains fully from using more while the cost is spread among all.",
          ],
          correct: 3,
        },
        {
          question: "How did Ostrom's approach differ from Hardin's?",
          options: [
            "She based her conclusions on studies of real communities.",
            "She focused only on fishing grounds.",
            "She argued that all resources should be privatized.",
            "She relied mainly on mathematical models.",
          ],
          correct: 0,
        },
        {
          question: "Why does the professor mention Swiss mountain pastures?",
          options: [
            "To show that overgrazing is common in Europe",
            "To give an example of a commons managed successfully for centuries",
            "To explain how pastures were privatized",
            "To describe where Hardin conducted his research",
          ],
          correct: 1,
        },
        {
          question: "What is the professor's view of Hardin's tragedy of the commons?",
          options: [
            "It is completely wrong and should be ignored.",
            "It applies only to pastures.",
            "It describes a real risk that can be avoided.",
            "It is a law of nature that cannot be changed.",
          ],
          correct: 2,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", W("Do you want to get lunch after class?"), [
      "I had a big breakfast.",
      "Sure, how about the café on Elm Street?",
      "Class ends at noon.",
      "I got it after class.",
    ], 1),
    response("low-r2", M("How long have you lived in this city?"), [
      "About three years now.",
      "It's a very long street.",
      "I live on the fourth floor.",
      "The city is very big.",
    ], 0),
    response("low-r3", W("Could you turn the music down a little?"), [
      "I bought the music yesterday.",
      "It's on the table.",
      "I turned left at the corner.",
      "Oh, sorry. Is that better?",
    ], 3),
    response("low-r4", M("Which bus goes to the airport?"), [
      "The airport is very busy.",
      "I'm flying next week.",
      "The number 12 does.",
      "It's a big bus.",
    ], 2),
    response("low-r5", W("Have you finished the math homework?"), [
      "I like math a lot.",
      "Almost. Just one problem left.",
      "The homework is on page six.",
      "Yes, I finished my dinner.",
    ], 1),
    {
      id: "low-ann",
      type: "announcement",
      title: "Cafeteria Announcement",
      lines: [
        M("Hello, everyone. Starting next Monday, the main cafeteria will open earlier, at seven a.m. instead of eight, so students can get breakfast before morning classes. The cafeteria will also add a new salad bar at lunchtime. Please remember that students with a meal plan must show their student ID card at the register. Thank you."),
      ],
      questions: [
        {
          question: "What change will happen next Monday?",
          options: [
            "The cafeteria will close earlier.",
            "Meal plans will cost more.",
            "The cafeteria will open earlier.",
            "Breakfast will no longer be served.",
          ],
          correct: 2,
        },
        {
          question: "What must students with a meal plan do?",
          options: [
            "Show their student ID card",
            "Arrive before seven a.m.",
            "Order lunch online",
            "Pay at the salad bar",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Geography: Why Deserts Get Cold at Night",
      lines: [
        W("Many people think deserts are always hot. But in many deserts, nights can be very cold, sometimes close to freezing. Why does this happen? The main reason is that the air in deserts is very dry. Water vapor in the air acts like a blanket. It holds heat close to the ground after the sun goes down. In humid places, this blanket keeps nights warm. But desert air has very little water vapor, so the heat escapes quickly into the sky. Deserts also have very few clouds, and clouds help trap heat too. That's why travelers in the desert need to bring warm clothes as well as sun protection."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How animals survive in deserts",
            "Why deserts become cold at night",
            "Where the largest deserts are located",
            "How clouds form over the ocean",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, what does water vapor in the air do?",
          options: [
            "It makes the sun stronger.",
            "It causes sandstorms.",
            "It cools the ground during the day.",
            "It holds heat near the ground.",
          ],
          correct: 3,
        },
        {
          question: "Why do humid places stay warmer at night?",
          options: [
            "They have more water vapor to trap heat.",
            "They receive more sunlight.",
            "They are closer to the equator.",
            "They have fewer plants.",
          ],
          correct: 0,
        },
        {
          question: "What advice does the professor give to desert travelers?",
          options: [
            "Travel only at night.",
            "Carry extra water vapor.",
            "Bring warm clothes as well as sun protection.",
            "Avoid cloudy days.",
          ],
          correct: 2,
        },
      ],
    },
  ],
};
